import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, Filter, Download, Upload, X } from 'lucide-react';
import { useDivisionUsers, useDeleteDivisionUser, useActivateDivisionUser, useExportDivisionUsers } from '../../hooks/use-division-users';
import { useDivisionContext } from '../../contexts/division-context';
import { useMatchIdentifiers } from '../../hooks/use-match-identifiers';
import { SidebarLayout, PageHeader, Card } from '../../components/layout';
import { Button } from '../../components/ui/button';
import { UserTable, UserFormModal, UserDetailModal, AddUserToDivisionModal } from '../../components/users';
import { UserImportModal } from '../../components/users/user-import-modal';
import { useAuth } from '../../hooks/use-auth';

export function UsersPage() {
  const { user, isAdmin, isSupport, isWorkflowAdmin } = useAuth();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [matchIdentifierFilter, setMatchIdentifierFilter] = useState<string>('');
  const [matchIdentifierValueFilter, setMatchIdentifierValueFilter] = useState('');
  const [hasMatchIdentifiersFilter, setHasMatchIdentifiersFilter] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('firstName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Use the global division context
  const { selectedDivisionId, setSelectedDivisionId, divisions } = useDivisionContext();

  // Get available match identifiers for the selected division
  const { data: matchIdentifiersData } = useMatchIdentifiers(
    selectedDivisionId,
    { active: true },
    !!selectedDivisionId
  );

  // Get users for selected division
  const { 
    data: usersData, 
    isLoading: isLoadingUsers, 
    error: usersError,
    refetch: refetchUsers
  } = useDivisionUsers(
    selectedDivisionId,
    {
      active: activeFilter,
      search: searchQuery || undefined,
      matchIdentifierId: matchIdentifierFilter || undefined,
      matchIdentifierValue: matchIdentifierValueFilter || undefined,
      hasMatchIdentifiers: hasMatchIdentifiersFilter,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    },
    !!selectedDivisionId
  );

  // Sort users client-side
  const sortedUsers = React.useMemo(() => {
    if (!usersData?.users) return [];
    
    const users = [...usersData.users];
    users.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      switch (sortBy) {
        case 'firstName':
          aVal = a.firstName?.toLowerCase() || '';
          bVal = b.firstName?.toLowerCase() || '';
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'externalUserId':
          aVal = a.externalUserId?.toLowerCase() || '';
          bVal = b.externalUserId?.toLowerCase() || '';
          break;
        case 'role':
          aVal = a.divisionRoles?.[0]?.role?.toLowerCase() || '';
          bVal = b.divisionRoles?.[0]?.role?.toLowerCase() || '';
          break;
        case 'active':
          aVal = a.active ? 1 : 0;
          bVal = b.active ? 1 : 0;
          break;
        case 'lastLogin':
          aVal = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
          bVal = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return users;
  }, [usersData?.users, sortBy, sortOrder]);

  const deleteUserMutation = useDeleteDivisionUser(selectedDivisionId);
  const activateUserMutation = useActivateDivisionUser(selectedDivisionId);
  const exportUsersMutation = useExportDivisionUsers(selectedDivisionId);

  // Focus on search input after data refresh
  useEffect(() => {
    if (!isLoadingUsers && searchInputRef.current && searchQuery) {
      searchInputRef.current.focus();
    }
  }, [isLoadingUsers, usersData]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await deleteUserMutation.mutateAsync(userId);
      console.log('User deleted successfully');
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleDeleteUsers = async (userIds: string[]) => {
    if (!window.confirm(`Are you sure you want to delete ${userIds.length} user${userIds.length !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      await Promise.all(userIds.map(id => deleteUserMutation.mutateAsync(id)));
      console.log(`${userIds.length} user(s) deleted successfully`);
    } catch (error) {
      console.error('Failed to delete users:', error);
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      await activateUserMutation.mutateAsync(userId);
      console.log('User activated successfully');
    } catch (error) {
      console.error('Failed to activate user:', error);
    }
  };

  const handleExportUsers = async (format: 'csv' | 'json' = 'csv') => {
    try {
      const blob = await exportUsersMutation.mutateAsync({
        format,
        active: activeFilter,
        includeMatchIdentifiers: true,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log(`Users exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to export users:', error);
    }
  };

  const handleImportComplete = () => {
    // Refetch will happen automatically via the hook
    setIsImportModalOpen(false);
  };


  const totalPages = usersData ? Math.ceil(usersData.totalCount / pageSize) : 0;

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <PageHeader
          title="User Management"
          subtitle="Manage users and their access within divisions"
        />

        {/* Filters and Actions */}
        <Card>
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Division Selector */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="min-w-0 flex-1 sm:max-w-xs">
                  <label htmlFor="division" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Division
                  </label>
                  <select
                    id="division"
                    value={selectedDivisionId}
                    onChange={(e) => {
                      setSelectedDivisionId(e.target.value);
                      setCurrentPage(1); // Reset to first page when changing division
                    }}
                    className="block w-full rounded-md border-gray-300 dark:border-secondary-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="">Select a division</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name} {division.code && `(${division.code})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search */}
                <div className="min-w-0 flex-1 sm:max-w-sm">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Search Users
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Search className="h-5 w-5 text-gray-400 dark:text-secondary-500" aria-hidden="true" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      id="search"
                      placeholder="Search by name, email..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1); // Reset to first page when searching
                      }}
                      data-testid="search-input"
                      className="block w-full rounded-md border-gray-300 dark:border-secondary-600 pl-10 focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>
                </div>

                {/* Active Filter */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-5 w-5 text-gray-400 dark:text-secondary-500" />
                    <select
                      value={activeFilter === undefined ? 'all' : activeFilter ? 'active' : 'inactive'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setActiveFilter(
                          value === 'all' ? undefined : value === 'active'
                        );
                        setCurrentPage(1); // Reset to first page when filtering
                      }}
                      className="rounded-md border-gray-300 dark:border-secondary-600 text-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    >
                      <option value="all">All Users</option>
                      <option value="active">Active Users</option>
                      <option value="inactive">Inactive Users</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                {(isAdmin || isWorkflowAdmin) && (
                  <Button
                    variant="outline"
                    onClick={() => setIsImportModalOpen(true)}
                    disabled={!selectedDivisionId}
                    className="flex items-center space-x-2"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Import</span>
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => handleExportUsers('csv')}
                  disabled={!selectedDivisionId || exportUsersMutation.isPending}
                  data-testid="export-csv-btn"
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export CSV</span>
                </Button>

                {!isSupport && (
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    disabled={!selectedDivisionId}
                    data-testid="add-user-btn"
                    className="flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add User</span>
                  </Button>
                )}
              </div>
            </div>


          </div>
        </Card>

        {/* Users Table */}
        {selectedDivisionId && (
          <Card>
            <UserTable
              users={sortedUsers}
              totalCount={usersData?.totalCount || 0}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              isLoading={isLoadingUsers}
              error={usersError}
              divisionId={selectedDivisionId}
              onPageChange={setCurrentPage}
              onUserClick={(userId: string) => setSelectedUserId(userId)}
              onDeleteUser={handleDeleteUser}
              onDeleteUsers={handleDeleteUsers}
              onActivateUser={handleActivateUser}
              onRefresh={refetchUsers}
              isDeleting={deleteUserMutation.isPending}
              isActivating={activateUserMutation.isPending}
              readOnly={isSupport}
              canDelete={isAdmin}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          </Card>
        )}

        {!selectedDivisionId && (
          <Card>
            <div className="p-12 text-center">
              <div className="text-gray-500 dark:text-secondary-400">
                <h3 className="text-lg font-medium mb-2">Select a Division</h3>
                <p>Please select a division to view and manage its users.</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Create User Modal */}
      {isCreateModalOpen && selectedDivisionId && (
        <UserFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          divisionId={selectedDivisionId}
          mode="create"
        />
      )}

      {/* User Detail Modal */}
      {selectedUserId && selectedDivisionId && (
        <UserDetailModal
          isOpen={!!selectedUserId}
          onClose={() => setSelectedUserId(null)}
          divisionId={selectedDivisionId}
          userId={selectedUserId}
          readOnly={isSupport}
        />
      )}

      {/* User Import Modal */}
      {isImportModalOpen && selectedDivisionId && (
        <UserImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          divisionId={selectedDivisionId}
          onImportComplete={handleImportComplete}
        />
      )}
    </SidebarLayout>
  );
}
